import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackAffiliateClick } from "@/utils/gtmTracking";

const preloadSamblaAnalysis = () => {
  void import("@/pages/SamblaPage");
};

const HeroSection = () => {
  const { currentLanguage } = useLanguage();

  return (
    <section
      id="hero-section"
      className="relative overflow-hidden pt-20 pb-8 md:pt-24 md:pb-10 lg:pt-28"
      style={{ backgroundColor: "hsl(var(--primary-dark))" }}
    >
      <div className="container relative z-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/75">
              Klarfinans
            </p>
            <h1 className="max-w-3xl text-3xl font-bold leading-tight text-white text-balance md:text-4xl lg:text-5xl">
              Hitta rätt låneväg snabbare.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-white/90 md:text-lg">
              Jämför långivare, se villkor och gå vidare när du är redo.
            </p>

            <p className="mt-4 max-w-2xl text-xs leading-5 text-white/75">
              <strong>Att låna kostar pengar.</strong> Säkerställ att du kan betala tillbaka innan du ansöker.
            </p>
          </div>

          <aside className="rounded-lg border border-white/15 bg-white p-4 text-slate-950 shadow-md md:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Partner i fokus</p>
                <h2 className="mt-1 text-xl font-bold">Sambla</h2>
              </div>
            </div>

            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Sambla är en låneförmedlare. Ansökan sker hos Sambla, inte på Klarfinans.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 border-y py-3">
              <div>
                <p className="text-xs text-muted-foreground">Långivare</p>
                <p className="mt-1 text-lg font-bold">40+</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Max belopp</p>
                <p className="mt-1 whitespace-nowrap text-lg font-bold">800 000 kr</p>
              </div>
            </div>

            <div className="mt-3 grid gap-2 text-sm">
              {["En ansökan", "En UC-kontroll", "Kostnadsfri jämförelse"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-2">
              <a
                href="https://go.adt284.net/t/t?a=1923302872&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
                onClick={() =>
                  trackAffiliateClick({
                    provider: "Sambla",
                    pageType: "homepage",
                    section: "hero-card",
                  })
                }
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-dark"
              >
                Gå till Sambla
                <ExternalLink className="h-4 w-4" />
              </a>
              <Link
                to={`/${currentLanguage}/privatlan/sambla`}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:underline"
                onFocus={preloadSamblaAnalysis}
                onMouseEnter={preloadSamblaAnalysis}
                onTouchStart={preloadSamblaAnalysis}
              >
                Läs analysen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
